import { gql, TypedDocumentNode } from '@apollo/client';

import {
  TimeEntriesConnection,
  TimeEntriesConnectionInput,
  TimeEntriesStatusConnection,
  TimeEntriesSubmitByUserInput,
  TimeEntriesUpdateByApproverInput,
  TimeEntryCreateInput,
  TimeEntryUpdate,
  WorkOrderTimeEntriesConnection,
  WorkOrderTimeEntry,
} from '@atom/types/timeEntry';

export const BASE_TIME_ENTRY = gql`
  fragment BaseTimeEntry on TimeEntry {
    id
    userId
    workOrderId
    taskId
    userGroupId
    userGroup {
      id
      name
      groupPath
    }
    status
    duration
    date
    type

    createdBy {
      firstName
      lastName
    }
    createdDate

    updatedBy {
      firstName
      lastName
    }
    updatedDate

    budget {
      id
      name
      rate
      restored
      reopenedWorkOrderIds
    }

    computedBudgets {
      id
      name
      rate
      duration
      restored
      reopenedWorkOrderIds
    }
    user {
      firstName
      lastName
    }
  }
`;

// Additional time entry properties that are used
// specifically in the time sheet UI
export const ADDITIONAL_TIME_ENTRY = gql`
  fragment AdditionalTimeEntry on TimeEntry {
    workOrder {
      id
      name
      priorityId
      statusId
      inventoryAssetId
      inventoryAssetName
      workTemplateId
      reopened
      createdBy {
        id
        firstName
        lastName
      }
      workTemplate {
        id
        name
      }
      dueDate
      completionDate
      completedBy {
        id
        firstName
        lastName
      }
      tasks {
        id
        name
        isCompleted
      }
      workTemplateFolderActions
      workTemplateFolderGrants {
        type
        ids
      }
    }
    task {
      id
      name
    }
  }
`;

// Additional timeEntries connection properties used to render
// specific parts of the time sheet UI.
export const TIME_SHEET_SUMMARY = gql`
  fragment TimeSheetSummary on TimeEntriesConnection {
    status
    totalDuration
    totalWorkTypes
    totalWorkOrders
    totalOpenWorkOrders
    dailyTotals {
      date
      duration
    }
    summary {
      title
      duration
    }
  }
`;

export const TIME_SHEET_ENTRIES = gql`
  fragment TimeSheetEntries on TimeEntriesConnection {
    totalCount
    ...TimeSheetSummary
    timeEntries {
      ...BaseTimeEntry
      ...AdditionalTimeEntry
    }
  }
  ${TIME_SHEET_SUMMARY}
  ${BASE_TIME_ENTRY}
  ${ADDITIONAL_TIME_ENTRY}
`;

export const GET_TIME_ENTRIES: TypedDocumentNode<
  { timeEntries: WorkOrderTimeEntriesConnection },
  { input: TimeEntriesConnectionInput }
> = gql`
  query timeEntries($input: TimeEntriesConnectionInput!) {
    timeEntries(input: $input) {
      totalCount
      timeEntries {
        ...BaseTimeEntry
      }
    }
  }
  ${BASE_TIME_ENTRY}
`;

export const GET_TIME_SHEET_ENTRIES: TypedDocumentNode<
  { timeEntries: TimeEntriesConnection },
  { input: TimeEntriesConnectionInput }
> = gql`
  query timeEntries($input: TimeEntriesConnectionInput!) {
    timeEntries(input: $input) {
      ...TimeSheetEntries
    }
  }
  ${TIME_SHEET_ENTRIES}
`;

export const GET_TIME_ENTRIES_STATUS: TypedDocumentNode<
  { timeEntries: TimeEntriesStatusConnection },
  { input: TimeEntriesConnectionInput }
> = gql`
  query timeEntries($input: TimeEntriesConnectionInput!) {
    timeEntries(input: $input) {
      totalCount
      status
    }
  }
`;

export const TIME_ENTRY_CREATE: TypedDocumentNode<
  { timeEntryCreate: WorkOrderTimeEntry },
  { input: TimeEntryCreateInput }
> = gql`
  mutation timeEntryCreate($input: TimeEntryCreateInput!) {
    timeEntryCreate(input: $input) {
      ...BaseTimeEntry
    }
  }
  ${BASE_TIME_ENTRY}
`;

export const TIME_ENTRIES_BULK_CREATE: TypedDocumentNode<
  { timeEntriesBulkCreate: boolean },
  { input: TimeEntryCreateInput[] }
> = gql`
  mutation timeEntriesBulkCreate($input: [TimeEntryCreateInput!]!) {
    timeEntriesBulkCreate(input: $input)
  }
`;

export const TIME_ENTRY_UPDATE: TypedDocumentNode<
  { timeEntryUpdate: WorkOrderTimeEntry },
  { input: TimeEntryUpdate }
> = gql`
  mutation timeEntryUpdate($input: TimeEntryUpdate!) {
    timeEntryUpdate(input: $input) {
      ...BaseTimeEntry
    }
  }
  ${BASE_TIME_ENTRY}
`;

export const TIME_ENTRY_DELETE: TypedDocumentNode<
  { timeEntryDelete: boolean },
  { id: string }
> = gql`
  mutation timeEntryDelete($id: ID!) {
    timeEntryDelete(id: $id)
  }
`;

export const TIME_ENTRIES_SUBMIT_BY_USER: TypedDocumentNode<
  { timeEntriesSubmitByUser: boolean },
  { input: TimeEntriesSubmitByUserInput }
> = gql`
  mutation timeEntriesSubmitByUser($input: TimeEntriesSubmitByUserInput!) {
    timeEntriesSubmitByUser(input: $input)
  }
`;

export const TIME_ENTRIES_UPDATE_BY_APPROVER: TypedDocumentNode<
  { timeEntriesUpdateByApprover: boolean },
  { input: TimeEntriesUpdateByApproverInput }
> = gql`
  mutation timeEntriesUpdateByApprover(
    $input: TimeEntriesUpdateByApproverInput!
  ) {
    timeEntriesUpdateByApprover(input: $input)
  }
`;
